import React from "react";
import { Button, Form, Modal, OverlayTrigger, Tooltip, Collapse } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { IdToken, useAuth0 } from "@auth0/auth0-react";

import settingsAppIcon from "../../assets/icons/settingsAppIcon.svg";
import { isAdminEmail, timestampToString, valueFromLocalStorage } from "../../utils/helpers";
import { apiVersionTC, circleRadius, configAgencyName, startFromAccordion, traccarCredentials } from "../../config";
import { useAppDispatch } from "../../redux/hooks";
import { dispatchMapSettings, dispatchStartWithAccordion } from "../../redux/actions";
import variableColors from "../../_App.module.scss";
import { HR } from "../MapBusesComponent";
import infoIcon from "../../assets/icons/infoIcon.svg";
import apiIcon from "../../assets/icons/apiIcon.svg";
import trueIcon from "../../assets/icons/trueIcon.svg";
import falseIcon from "../../assets/icons/falseIcon.svg";
import speedUnitIcon from "../../assets/icons/speedUnitIcon.svg";
import timeZoneIcon from "../../assets/icons/timeZoneIcon.svg";
import accountTypeIcon from "../../assets/icons/accountTypeIcon.svg";
import { AgencyContext } from "../../hooks/AgencyContext";
import SelectTimeZone from "./SelectTimeZone";
import EarlyLateTimeSettings from "./EarlyLateTimeSettings";
import SettingsSetCircleRadius from "./SettingsSetCircleRadius";

const { dangerColor, successColor } = variableColors;

export const SwitchDiv = styled.div<{ $spanColor?: string }>`
  padding: 0 1rem;
  form {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    align-content: center;
    gap: 0.25rem;
    border: none;
  }
  form.radio-form {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    align-content: center;
  }
  button {
    margin-top: 1rem;
    padding: 2px 4px;
    font-weight: 500;
  }
  div#Show-more-details {
    p.text-muted.small {
      font-size: 80%;
      margin-bottom: 2px;
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: center;
      align-content: center;
      gap: 0.25rem;
      a {
        text-decoration: none;
        opacity: 0.85;
        padding: 0;
      }
      span.validity-token {
        color: ${(props) => (props?.$spanColor ? props?.$spanColor : "inherit")};
      }
      &:first-of-type {
        margin-top: 0.5rem;
      }
    }
  }
`;

const SettingsModal = ({
  allowableLateRedux,
  allowableEarlyRedux,
  setAllowableLateRedux,
  setAllowableEarlyRedux,
}: {
  allowableLateRedux: number;
  allowableEarlyRedux: number;
  setAllowableLateRedux: React.Dispatch<React.SetStateAction<number>>;
  setAllowableEarlyRedux: React.Dispatch<React.SetStateAction<number>>;
}): JSX.Element => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, getIdTokenClaims, user } = useAuth0();

  const dispatch: Dispatch = useAppDispatch();

  //* AgencyContext
  const { agencyName, agencyId, url, lang, timeZone, timeZoneOffsetMinutes, speedUnit } = React.useContext(
    AgencyContext
  ) as AgencyContextType;

  const [openCollapse, setOpenCollapse] = React.useState<boolean>(false);
  const [tokenInfo, setTokenInfo] = React.useState<{ tokenRaw: string; tokenExp: number }>({ tokenRaw: "", tokenExp: 0 });
  const [showModal, setShowModal] = React.useState(false);
  const [showLineNumbers, setShowLineNumbers] = React.useState<boolean>(
    valueFromLocalStorage("showLineNumbers") ? JSON.parse(valueFromLocalStorage("showLineNumbers") as string) : true
  );
  const [showDeviations, setShowDeviations] = React.useState<boolean>(
    valueFromLocalStorage("showDeviations") ? JSON.parse(valueFromLocalStorage("showDeviations") as string) : true
  );
  const [colorMap, setColorMap] = React.useState<boolean>(
    valueFromLocalStorage("colorMap") ? JSON.parse(valueFromLocalStorage("colorMap") as string) : true
  );
  const [showAllRoutes, setShowAllRoutes] = React.useState<boolean>(
    valueFromLocalStorage("showAllRoutes") ? JSON.parse(valueFromLocalStorage("showAllRoutes") as string) : false
  );
  const [showTechnicalVehicles, setShowTechnicalVehicles] = React.useState<boolean>(
    valueFromLocalStorage("showTechnicalVehicles")
      ? JSON.parse(valueFromLocalStorage("showTechnicalVehicles") as string)
      : false
  );
  const [showControls, setShowControls] = React.useState<boolean>(
    valueFromLocalStorage("showControls") ? JSON.parse(valueFromLocalStorage("showControls") as string) : true
  );
  //* Only Local Storage!
  const [isStartingFromAccordion, setIsStartingFromAccordion] = React.useState<boolean>(
    valueFromLocalStorage("isStartingFromAccordion")
      ? JSON.parse(valueFromLocalStorage("isStartingFromAccordion") as string)
      : startFromAccordion
  );
  const [shouldReloadWindow, setShouldReloadWindow] = React.useState<boolean>(false);

  //* [km] -> initial value: 5km
  const [circleRadiusSettings, setCircleRadiusSettings] = React.useState<number>(
    valueFromLocalStorage("circleRadiusSettings")
      ? Number(JSON.parse(valueFromLocalStorage("circleRadiusSettings") as string))
      : (circleRadius / 1000) * 10
  );
  // console.log("circleRadiusSettings:", circleRadiusSettings);

  //* Check if is admin
  const isAdmin: boolean = isAdminEmail(user?.email as string);
  // console.log({ isAdmin });

  const handleClose = (): void => {
    setShowModal(false);
    if (shouldReloadWindow) {
      // V1
      // window.location.reload();
      // V2
      navigate(0);
    }
  };
  const handleShow = (): void => setShowModal(true);

  //* Save settings in LocalStorage
  React.useEffect(() => {
    const localStorageSettings = {
      showLineNumbers,
      showDeviations,
      colorMap,
      showAllRoutes,
      showTechnicalVehicles,
      showControls,
      isStartingFromAccordion,
      circleRadiusSettings,
    };
    Object.entries(localStorageSettings).forEach((entry: [string, boolean | number]) =>
      localStorage.setItem(entry[0], JSON.stringify(entry[1]))
    );
  }, [
    colorMap,
    isStartingFromAccordion,
    showAllRoutes,
    showControls,
    showDeviations,
    showLineNumbers,
    showTechnicalVehicles,
    circleRadiusSettings,
  ]);

  //* Dispatch map settings to the Redux State
  React.useEffect(() => {
    const mapSettings = {
      showLineNumbers,
      showDeviations,
      colorMap,
      showAllRoutes,
      showTechnicalVehicles,
      showControls,
      circleRadiusSettings,
    };
    // console.log("mapSettings:", mapSettings);
    dispatch(dispatchMapSettings(mapSettings));
  }, [
    colorMap,
    dispatch,
    showAllRoutes,
    showControls,
    showDeviations,
    showLineNumbers,
    showTechnicalVehicles,
    circleRadiusSettings,
  ]);

  //* Dispatch startWithAccordion settings to the Redux State
  React.useEffect(() => {
    dispatch(dispatchStartWithAccordion(isStartingFromAccordion));
  }, [dispatch, isStartingFromAccordion]);

  // Get token info
  React.useEffect(() => {
    if (!isAuthenticated) return;
    if (isAuthenticated) {
      (async function (): Promise<void> {
        const token = (await getIdTokenClaims()) as IdToken;
        let { __raw, exp } = token as { __raw: string; exp: number };
        // console.log({ __raw, exp });
        if (String(exp).length === 10) {
          exp = exp * 1000;
        }
        setTokenInfo({ tokenRaw: __raw, tokenExp: exp });
      })();
    }
  }, [getIdTokenClaims, isAuthenticated]);

  return (
    <React.Fragment>
      <OverlayTrigger placement={"bottom"} overlay={<Tooltip id={"headerTooltipSettings"}>{t("Settings")}</Tooltip>}>
        <Button variant="dark" onClick={handleShow} size="sm">
          <img src={settingsAppIcon} alt="Settings icon" width={"auto"} height={18} />
        </Button>
      </OverlayTrigger>

      <Modal
        size="lg"
        show={showModal}
        onHide={handleClose}
        fullscreen={"lg-down"}
        dialogClassName=""
        centered={true}
        backdrop={false}
        keyboard={true}
      >
        <Modal.Header closeButton className="bg-secondary text-light" closeVariant="white">
          <Modal.Title>{t("Settings")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <SwitchDiv $spanColor={tokenInfo?.tokenExp ? successColor : dangerColor}>
            <Form>
              <Form.Check
                className="custom_switch"
                inline={false}
                label={t("Pokaż podpowiedzi")}
                type="checkbox"
                id="switch_show_buses_labels"
                onChange={() => setShowLineNumbers(!showLineNumbers)}
                checked={showLineNumbers}
              />
              <Form.Check
                inline={false}
                label={t("Pokaż odchylenia na mapie")}
                type="checkbox"
                id="show_deviations"
                onChange={() => setShowDeviations(!showDeviations)}
                checked={showDeviations}
              />
              <Form.Check
                className="custom_switch1"
                inline={false}
                label={t("Kolorowa mapa")}
                type="checkbox"
                id="show_color_map"
                onChange={() => setColorMap(!colorMap)}
                checked={colorMap}
              />
              <Form.Check
                className="custom_switch2"
                inline={false}
                label={t("Wszystkie_trasy")}
                type="checkbox"
                id="show_all_routes"
                onChange={() => setShowAllRoutes(!showAllRoutes)}
                checked={showAllRoutes}
                disabled={pathname !== "/" ? true : false}
              />

              {traccarCredentials ? (
                <Form.Check
                  className="custom_switch3"
                  inline={false}
                  label={t("Techniczne_pojazdy")}
                  type="checkbox"
                  id="show_technical_vehicles"
                  onChange={() => setShowTechnicalVehicles(!showTechnicalVehicles)}
                  checked={showTechnicalVehicles}
                />
              ) : null}
            </Form>
            <HR />

            <Form>
              <Form.Check
                className="custom_switch4"
                inline={false}
                label={t("Pokaż_kontrolki")}
                type="checkbox"
                id="show_controls"
                onChange={() => setShowControls(!showControls)}
                checked={showControls}
              />
            </Form>

            <Form className="radio-form">
              {`${t("Zacznij od")}: `}
              <Form.Check
                inline={true}
                disabled={configAgencyName === ("PoznanPKS" as string) ? false : true}
                label={t("On-time Performance")}
                type={"radio"}
                id={"start_from_accordion_1"}
                onChange={() => setIsStartingFromAccordion(!isStartingFromAccordion)}
                checked={!isStartingFromAccordion}
                className="custom_switch5"
              />
              <Form.Check
                inline={true}
                disabled={configAgencyName === ("PoznanPKS" as string) ? false : true}
                label={t("Live Blocks (accordion)")}
                type={"radio"}
                id={"start_from_accordion_2"}
                onChange={() => setIsStartingFromAccordion(!isStartingFromAccordion)}
                checked={isStartingFromAccordion}
                className="custom_switch5"
              />
            </Form>

            <HR />
            <br />
            <EarlyLateTimeSettings
              allowableLateRedux={allowableLateRedux}
              allowableEarlyRedux={allowableEarlyRedux}
              setAllowableLateRedux={setAllowableLateRedux}
              setAllowableEarlyRedux={setAllowableEarlyRedux}
              setShouldReloadWindow={setShouldReloadWindow}
            />
            <HR />
            <br />
            <SettingsSetCircleRadius
              circleRadiusSettings={circleRadiusSettings}
              setCircleRadiusSettings={setCircleRadiusSettings}
              circleRadius={circleRadius}
              setShouldReloadWindow={setShouldReloadWindow}
            />
            <HR />
            <br />
            <SelectTimeZone />
            <HR />
            <br />

            <Button
              className="w-100"
              size="sm"
              onClick={() => setOpenCollapse(!openCollapse)}
              aria-controls="Show-more-details"
              aria-expanded={openCollapse}
            >
              {t("Szczegóły techniczne")}
            </Button>
            <Collapse in={openCollapse}>
              <div id="Show-more-details">
                <p className="text-muted small">
                  <img src={infoIcon} alt="Info Icon" height={16} width={"auto"} /> {t("Ikony pobrano ze strony")}{" "}
                  <Button variant="link" href="https://www.iconfinder.com" target="_blank" size="sm">
                    https://www.iconfinder.com
                  </Button>
                </p>
                <p className="text-muted small">
                  <img src={apiIcon} alt="API Icon" height={16} width={"auto"} /> {t("apiVersionTC")}{" "}
                  <span className="span_bold">{apiVersionTC}</span>
                </p>
                <p className="text-muted small">
                  <img src={window.location.origin + "/bus.svg"} alt="Bus icon" height={16} width={"auto"} /> {t("carrier")}{" "}
                  <span>
                    <span className="span_bold">{agencyName}</span>
                    <wbr />{" "}
                    <span>
                      <a href={url} target="_blank" rel="noreferrer">
                        {url}
                      </a>{" "}
                      {`(${agencyId})`}
                    </span>
                  </span>
                </p>
                <p className="text-muted small">
                  <img
                    src={Date.now() <= tokenInfo?.tokenExp ? trueIcon : falseIcon}
                    alt="Valid/Expired Icon"
                    height={"auto"}
                    width={16}
                  />{" "}
                  <a href="https://jwt.io/" target="_blank" rel="noreferrer">
                    {"JWT Token"}
                  </a>{" "}
                  {t("tokenValidDate")}{" "}
                  <span className="span_bold validity-token">{timestampToString(tokenInfo?.tokenExp)}</span>
                </p>
                <p className="text-muted small">
                  <img src={timeZoneIcon} alt="Time zone icon" height={16} width={"auto"} />{" "}
                  {t("Time zone / UTC Offset / Language")}{" "}
                  <span className="span_bold">{`${timeZone} / ${timeZoneOffsetMinutes} / ${lang}`}</span>
                </p>
                <p className="text-muted small">
                  <img src={accountTypeIcon} alt="Account type icon" height={16} width={"auto"} /> {t("Account type")}{" "}
                  <span className="span_bold">{isAdmin ? t("Admin") : t("User")}</span>
                </p>
                <p className="text-muted small">
                  <img src={speedUnitIcon} alt="Speed unit icon" height={16} width={"auto"} /> {t("Jednostka prędkości")}{" "}
                  <span className="span_bold">{speedUnit === "kmh" ? "km/h" : speedUnit === "mph" ? "mph" : t("b/d")}</span>
                </p>
              </div>
            </Collapse>
          </SwitchDiv>
        </Modal.Body>
        <Modal.Footer className="bg-secondary text-light">
          <Button variant="dark" onClick={handleClose}>
            {"Ok"}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default SettingsModal;
